import { ActionIcon, Badge, Button, Group, Modal, NumberInput, Select, Stack, Table, Tabs, Text, TextInput, Title } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Schemas, api, call, hasPermission } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert, Loading, formatTs, notifyError, notifyOk } from "../../components/common";
import { JobTimeCorrection } from "./JobTimeCorrection";

/** Effective time (used for attribution); the recorded click time is shown when it differs. */
function JobTime({ effective, recorded }: { effective: string | null; recorded: string | null }) {
  return (
    <>
      {formatTs(effective)}
      {recorded && effective && recorded !== effective && (
        <Text size="xs" c="dimmed">corrected · recorded {formatTs(recorded)}</Text>
      )}
    </>
  );
}

const JOB_STATUS_COLORS: Record<string, string> = {
  planned: "gray", running: "green", paused: "yellow", completed: "blue", cancelled: "red",
};

export function ProductsPage() {
  return (
    <Stack>
      <div>
        <Title order={2}>Products & jobs</Title>
        <Text size="sm" c="dimmed">What is produced, the ideal rate per machine (basis of OEE performance), and production jobs.</Text>
      </div>
      <Tabs defaultValue="products">
        <Tabs.List>
          <Tabs.Tab value="products">Products</Tabs.Tab>
          <Tabs.Tab value="jobs">Jobs</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="products" pt="md"><Products /></Tabs.Panel>
        <Tabs.Panel value="jobs" pt="md"><Jobs /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function Products() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const canManage = hasPermission(me, "masterdata:manage");
  const [rates, setRates] = useState<string | null>(null);
  const [form, setForm] = useState({ code: "", name: "", unit: "pcs", ideal_cycle_time_s: "" as number | "" });
  const products = useQuery({ queryKey: ["products"], queryFn: () => call(api.GET("/api/v1/products")) });
  const add = useMutation({
    mutationFn: () => call(api.POST("/api/v1/products", {
      body: { ...form, ideal_cycle_time_s: form.ideal_cycle_time_s === "" ? null : form.ideal_cycle_time_s },
    })),
    onSuccess: () => { setForm({ code: "", name: "", unit: "pcs", ideal_cycle_time_s: "" }); void qc.invalidateQueries({ queryKey: ["products"] }); },
    onError: notifyError,
  });
  if (products.isLoading) return <Loading />;
  if (products.error) return <ErrorAlert error={products.error} />;
  return (
    <Stack>
      <Table striped>
        <Table.Thead>
          <Table.Tr><Table.Th>Code</Table.Th><Table.Th>Name</Table.Th><Table.Th>Unit</Table.Th><Table.Th>Default ideal cycle</Table.Th><Table.Th /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {products.data!.map((p) => (
            <Table.Tr key={p.id}>
              <Table.Td>{p.code}</Table.Td><Table.Td>{p.name}</Table.Td><Table.Td>{p.unit}</Table.Td>
              <Table.Td>{p.ideal_cycle_time_s ? `${p.ideal_cycle_time_s} s` : "—"}</Table.Td>
              <Table.Td><Button size="compact-xs" variant="default" onClick={() => setRates(p.id)}>Rates per machine</Button></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {products.data!.length === 0 && <Text size="sm" c="dimmed">No products yet.</Text>}
      {canManage && (
        <Group align="end">
          <TextInput size="xs" label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value })} />
          <TextInput size="xs" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <TextInput size="xs" label="Unit" w={80} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.currentTarget.value })} />
          <NumberInput size="xs" label="Ideal cycle (s)" w={120} min={0} decimalScale={3} value={form.ideal_cycle_time_s}
            onChange={(v) => setForm({ ...form, ideal_cycle_time_s: v === "" ? "" : Number(v) })} />
          <Button size="xs" disabled={!form.code || !form.name} loading={add.isPending} onClick={() => add.mutate()}>Add product</Button>
        </Group>
      )}
      <Modal opened={rates !== null} onClose={() => setRates(null)} size="lg" title="Ideal rates">
        {rates && <RatesEditor productId={rates} canManage={canManage} onDone={() => setRates(null)} />}
      </Modal>
    </Stack>
  );
}

function RatesEditor({ productId, canManage, onDone }: { productId: string; canManage: boolean; onDone: () => void }) {
  const qc = useQueryClient();
  const product = useQuery({
    queryKey: ["product", productId],
    queryFn: () => call(api.GET("/api/v1/products/{product_id}", { params: { path: { product_id: productId } } })),
  });
  const types = useQuery({ queryKey: ["machine-types"], queryFn: () => call(api.GET("/api/v1/machine-types")) });
  const machines = useQuery({ queryKey: ["machines"], queryFn: () => call(api.GET("/api/v1/machines")) });
  const [rows, setRows] = useState<Schemas["ProductRateIn"][] | null>(null);
  const current = rows ?? product.data?.rates.map(({ id: _id, ...r }) => r) ?? [];
  const targets = [
    ...(types.data ?? []).map((t) => ({ value: `type:${t.id}`, label: `Type: ${t.name}` })),
    ...(machines.data ?? []).map((m) => ({ value: `machine:${m.id}`, label: `Machine: ${m.code} ${m.name}` })),
  ];
  const target = (r: Schemas["ProductRateIn"]) => (r.machine_id ? `machine:${r.machine_id}` : r.machine_type_id ? `type:${r.machine_type_id}` : null);
  const setRow = (i: number, patch: Partial<Schemas["ProductRateIn"]>) => setRows(current.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = useMutation({
    mutationFn: () => call(api.PUT("/api/v1/products/{product_id}/rates", {
      params: { path: { product_id: productId } }, body: { rates: current },
    })),
    onSuccess: () => { notifyOk("Rates saved"); void qc.invalidateQueries({ queryKey: ["product", productId] }); onDone(); },
    onError: notifyError,
  });
  if (product.isLoading) return <Loading />;
  return (
    <Stack>
      <Text size="sm">{product.data?.name}: the most specific rate wins (machine → machine type → product default).</Text>
      <Table>
        <Table.Thead><Table.Tr><Table.Th>Applies to</Table.Th><Table.Th>Ideal cycle (s)</Table.Th><Table.Th>Units per cycle</Table.Th><Table.Th /></Table.Tr></Table.Thead>
        <Table.Tbody>
          {current.map((r, i) => (
            <Table.Tr key={i}>
              <Table.Td><Select size="xs" disabled={!canManage} data={targets} value={target(r)} onChange={(v) => {
                const [kind, id] = (v ?? ":").split(":");
                setRow(i, { machine_type_id: kind === "type" ? id : null, machine_id: kind === "machine" ? id : null });
              }} /></Table.Td>
              <Table.Td><NumberInput size="xs" w={110} disabled={!canManage} min={0} decimalScale={3} value={r.ideal_cycle_time_s}
                onChange={(v) => setRow(i, { ideal_cycle_time_s: Number(v) })} /></Table.Td>
              <Table.Td><NumberInput size="xs" w={90} disabled={!canManage} min={1} value={r.units_per_cycle ?? 1}
                onChange={(v) => setRow(i, { units_per_cycle: Number(v) || 1 })} /></Table.Td>
              <Table.Td>{canManage && <ActionIcon variant="subtle" color="red" aria-label="Remove rate"
                onClick={() => setRows(current.filter((_, j) => j !== i))}><IconTrash size={16} /></ActionIcon>}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {canManage && (
        <Group justify="space-between">
          <Button size="xs" variant="default" leftSection={<IconPlus size={14} />}
            onClick={() => setRows([...current, { ideal_cycle_time_s: 60, units_per_cycle: 1, machine_type_id: null, machine_id: null }])}>Add rate</Button>
          <Button size="xs" loading={save.isPending} onClick={() => save.mutate()}>Save rates</Button>
        </Group>
      )}
    </Stack>
  );
}

function Jobs() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const canManage = hasPermission(me, "job:manage");
  const canCorrect = hasPermission(me, "job:correct");
  const [correcting, setCorrecting] = useState<Schemas["JobOut"] | null>(null);
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => call(api.GET("/api/v1/jobs")) });
  const products = useQuery({ queryKey: ["products"], queryFn: () => call(api.GET("/api/v1/products")) });
  const machines = useQuery({ queryKey: ["machines"], queryFn: () => call(api.GET("/api/v1/machines")) });
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const [form, setForm] = useState({ factory_id: "", code: "", product_id: "", machine_id: "", planned_qty: "" as number | "" });
  const create = useMutation({
    mutationFn: () => call(api.POST("/api/v1/jobs", { body: {
      factory_id: form.factory_id || factories.data![0].id, code: form.code,
      product_id: form.product_id || null, machine_id: form.machine_id || null,
      planned_qty: form.planned_qty === "" ? null : form.planned_qty,
    } })),
    onSuccess: () => { setForm({ ...form, code: "", planned_qty: "" }); void qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: notifyError,
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Schemas["JobUpdate"]["status"] }) =>
      call(api.PATCH("/api/v1/jobs/{job_id}", { params: { path: { job_id: id } }, body: { status } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["jobs"] }),
    onError: notifyError,
  });
  if (jobs.isLoading) return <Loading />;
  if (jobs.error) return <ErrorAlert error={jobs.error} />;
  const productName = (id?: string | null) => products.data?.find((p) => p.id === id)?.code ?? "—";
  const machineName = (id?: string | null) => machines.data?.find((m) => m.id === id)?.code ?? "—";
  return (
    <Stack>
      <Table.ScrollContainer minWidth={700}>
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Job</Table.Th><Table.Th>Product</Table.Th><Table.Th>Machine</Table.Th><Table.Th>Planned</Table.Th>
            <Table.Th>Status</Table.Th><Table.Th>Started</Table.Th><Table.Th>Ended</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {jobs.data!.map((j) => (
              <Table.Tr key={j.id}>
                <Table.Td>{j.code}</Table.Td><Table.Td>{productName(j.product_id)}</Table.Td><Table.Td>{machineName(j.machine_id)}</Table.Td>
                <Table.Td>{j.planned_qty ?? "—"}</Table.Td>
                <Table.Td>
                  {canManage ? (
                    <Select size="xs" w={130} data={Object.keys(JOB_STATUS_COLORS)} value={j.status}
                      onChange={(v) => v && setStatus.mutate({ id: j.id, status: v as Schemas["JobUpdate"]["status"] })} />
                  ) : <Badge color={JOB_STATUS_COLORS[j.status]}>{j.status}</Badge>}
                </Table.Td>
                <Table.Td><JobTime effective={j.effective_start} recorded={j.actual_start} /></Table.Td>
                <Table.Td><JobTime effective={j.effective_end} recorded={j.actual_end} /></Table.Td>
                <Table.Td>
                  {canCorrect && j.effective_start && (
                    <Button size="xs" variant="subtle" onClick={() => setCorrecting(j)}>Correct times</Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {jobs.data!.length === 0 && <Text size="sm" c="dimmed">No jobs yet.</Text>}
      {correcting && <JobTimeCorrection job={correcting} onClose={() => setCorrecting(null)} />}
      {canManage && (
        <Group align="end">
          {(factories.data?.length ?? 0) > 1 && (
            <Select size="xs" label="Factory" data={(factories.data ?? []).map((f) => ({ value: f.id, label: f.name }))}
              value={form.factory_id || null} onChange={(v) => setForm({ ...form, factory_id: v ?? "", machine_id: "" })} />
          )}
          <TextInput size="xs" label="Job / order no." value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value })} />
          <Select size="xs" label="Product" clearable data={(products.data ?? []).map((p) => ({ value: p.id, label: `${p.code} ${p.name}` }))}
            value={form.product_id || null} onChange={(v) => setForm({ ...form, product_id: v ?? "" })} />
          <Select size="xs" label="Machine" clearable
            data={(machines.data ?? []).filter((m) => !form.factory_id || m.factory_id === form.factory_id).map((m) => ({ value: m.id, label: m.code }))}
            value={form.machine_id || null} onChange={(v) => setForm({ ...form, machine_id: v ?? "" })} />
          <NumberInput size="xs" label="Planned qty" w={110} min={0} value={form.planned_qty}
            onChange={(v) => setForm({ ...form, planned_qty: v === "" ? "" : Number(v) })} />
          <Button size="xs" disabled={!form.code || !factories.data?.length} loading={create.isPending} onClick={() => create.mutate()}>Create job</Button>
        </Group>
      )}
    </Stack>
  );
}
